/**
 * Which tax years we build, and therefore sell (owner decision T6, 2026-10-14:
 * "cover only latest form years; rest, don't show from now on").
 *
 * The registry's forms and rates are the 2025 revisions, each for tax years
 * beginning in 2025: i1120 2025 p. 8 "Period Covered"
 * (https://www.irs.gov/pub/irs-pdf/i1120.pdf), i1065 2025 p. 5 "Period Covered"
 * (https://www.irs.gov/pub/irs-pdf/i1065.pdf), and the 2025 Form 1040-NR header
 * "For the year Jan. 1-Dec. 31, 2025". A single-member LLC without ECI files
 * Form 5472 (Rev. December 2023, the revision for tax years 2023 on) with a pro
 * forma Form 1120 page 1 on that year's own revision, so it is also built for
 * 2023 and 2024. 2020-2022 were filed on Form 5472 Rev. 12-2020, 12-2021 and
 * 12-2022 (other line numbers; line 42 not yet split), so they would be
 * prepared by hand and are not offered to new customers. A multi-member LLC
 * (PG-22) is built for 2023 and 2024 on those years' Form 1065, Schedule K-1
 * and K-2/K-3 revisions (`@nt/irs-forms` `F1065_FORMS_BY_YEAR`); every such
 * return is late, so Schedule B question 4 is "No" and Schedules L, M-1, M-2
 * and the page-1 K-2/K-3 are filed (research/mmllc-k2k3.md, "PG-22").
 * Existing orders for any year keep working (`buildsTaxYear` sends an unbuilt
 * year to the team).
 *
 * The API Worker (`apps/api/src/federal-tax/supported-tax-year.ts`), the customer
 * app and the qualification read these, so one list decides what is built and
 * what is sold. Moving to the next form year is one bump of `SUPPORTED_TAX_YEAR`
 * once the registry carries the new revisions.
 */
import { irsCalendarYear } from "./deadlines";
import {
  ENTITY_KINDS,
  SCENARIOS,
  entityKindOfScenario,
  isEntityKind,
  type EntityKind,
  type Scenario,
} from "./scenario";

/** The one tax year every scenario's forms and rates cover. */
export const SUPPORTED_TAX_YEAR = 2025;

/** Prior tax years the Worker builds, per scenario (see the module comment). */
export const PRIOR_YEARS_BUILT: Readonly<Record<string, readonly number[]>> = {
  smllc_no_eci: [2023, 2024],
  mmllc: [2023, 2024],
};

/** Whether the Worker builds this scenario's return for `year`; an unknown scenario builds only the supported year. */
export function buildsTaxYear(
  scenario: Scenario | null | undefined,
  year: number,
): boolean {
  if (year === SUPPORTED_TAX_YEAR) return true;
  if (!scenario) return false;
  return (PRIOR_YEARS_BUILT[scenario] ?? []).includes(year);
}

/**
 * The current filing season's tax year (last year on the IRS calendar) is
 * always sold, built or not: refusing the season itself because the next form
 * revision is not in yet would stop every filing from Jan 1 until the bump.
 * Until `SUPPORTED_TAX_YEAR` catches up it is prepared by hand (`await_manual`).
 */
function seasonYear(now: Date | undefined): number | null {
  return now ? irsCalendarYear(now) - 1 : null;
}

function withSeason(years: number[], now: Date | undefined): number[] {
  const season = seasonYear(now);
  const all =
    season !== null && !years.includes(season) ? [...years, season] : years;
  return [...all].sort((a, b) => b - a);
}

/** The tax years a customer may start or buy for this scenario, newest first (with `now`, the season too). */
export function offeredTaxYears(scenario: Scenario, now?: Date): number[] {
  return withSeason(
    [SUPPORTED_TAX_YEAR, ...(PRIOR_YEARS_BUILT[scenario] ?? [])],
    now,
  );
}

/**
 * The tax years offered for a billing kind, newest first: the union of its
 * scenarios. The year is chosen before qualification resolves the scenario, so
 * the picker and the checkout check by kind and qualification refuses a year
 * the resolved scenario is not built for.
 */
export function offeredTaxYearsForKind(kind: EntityKind, now?: Date): number[] {
  const years = new Set<number>();
  for (const scenario of SCENARIOS) {
    if (entityKindOfScenario(scenario) !== kind) continue;
    for (const year of offeredTaxYears(scenario)) years.add(year);
  }
  return withSeason([...years], now);
}

/** `year` is offered for this scenario, or (given a billing kind) for some scenario of it. Pass `now` so the current season counts. */
export function isTaxYearOffered(
  scenarioOrKind: Scenario | EntityKind,
  year: number,
  now?: Date,
): boolean {
  const years = isEntityKind(scenarioOrKind)
    ? offeredTaxYearsForKind(scenarioOrKind, now)
    : offeredTaxYears(scenarioOrKind, now);
  return years.includes(year);
}

/**
 * A tax year is open when some kind of company may start it (with `now`, the
 * current season always counts). The tax-year waitlist (PG-13) mails its leads
 * once their year is open.
 */
export function isTaxYearOpen(year: number, now: Date): boolean {
  return ENTITY_KINDS.some((kind) =>
    offeredTaxYearsForKind(kind, now).includes(year),
  );
}

/**
 * The tax years a lead may wait for, oldest first: newer than every open year
 * and no later than `now`'s calendar year. An old year we do not build is
 * prepared by hand and will never open, so it is never promised.
 */
export function waitlistTaxYears(now: Date): number[] {
  const newestOpen = Math.max(
    ...ENTITY_KINDS.flatMap((kind) => offeredTaxYearsForKind(kind, now)),
  );
  const years: number[] = [];
  for (let year = newestOpen + 1; year <= irsCalendarYear(now); year += 1) {
    years.push(year);
  }
  return years;
}

/** Whether a lead may join the waitlist for `year` (see `waitlistTaxYears`). */
export function canJoinTaxYearWaitlist(year: number, now: Date): boolean {
  return Number.isInteger(year) && waitlistTaxYears(now).includes(year);
}

/** "2023, 2024 and 2025". */
function yearList(years: readonly number[]): string {
  const sorted = [...years].sort((a, b) => a - b).map(String);
  return sorted.length < 2
    ? (sorted[0] ?? "")
    : `${sorted.slice(0, -1).join(", ")} and ${sorted[sorted.length - 1]}`;
}

/**
 * The sentence every refusal of an unoffered year carries (qualification copy, the API's 400).
 * Pass `now` so the current season, which is always sold, is named too.
 */
export function offeredTaxYearsSentence(now?: Date): string {
  const others = withSeason([SUPPORTED_TAX_YEAR], now);
  // The single-member LLC without ECI and the multi-member LLC are built for the same years.
  return `We prepare tax years ${yearList(
    offeredTaxYears("smllc_no_eci", now),
  )} for a single-member LLC with no US business activity or a multi-member LLC, and tax year${
    others.length > 1 ? "s" : ""
  } ${yearList(others)} for every other company.`;
}
